import styled from '@emotion/styled'
import {
  Box,
  Button,
  buttonClasses,
  Chip,
  FormControl,
  Grid,
  Snackbar,
  Tooltip,
  tooltipClasses,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClasses,
  IconButton
} from '@mui/material'
import {ArrowForwardIosSharp, Close, InfoOutlined, Report, KeyboardArrowDown} from '@mui/icons-material'

export const StyledPopper = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.hpWhite,
  minWidth: 'auto',
  fontSize: theme.typography.subtitle1.fontSize,
  marginLeft: '7px',
  border: '1px solid',
  borderColor: theme.palette.grey[200],
  borderRadius: '4px',
  position: 'absolute',
  zIndex: 1,
  transform: 'translateY(0px)',
  padding: theme.spacing(1),
  maxHeight: '400px',
  overflowY: 'auto'
}))

export const StyledContentBox = styled(Box)(() => ({
  padding: '0px 32px'
}))

export const StyledBox = styled(Box)(({theme}) => ({
  padding: '0px 8px 0px 32px',
  margin: '0px -8px',
  width: 'calc(100% + 16px)',
  boxSizing: 'border-box',
  '&:hover': {
    backgroundColor: theme.palette.hpPrimaryLightest
  }
}))

export const StyledDropDown = styled(Button)(({theme}) => ({
  width: '210px',
  height: '32px',
  border: '1px solid',
  borderColor: theme.palette.grey[200],
  color: theme.palette.hpAccentBlack,
  textTransform: 'none',
  textAlign: 'left',
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.subtitle1.fontSize,
  fontFamily: theme.typography.fontFamily,
  lineHeight: '16px',
  padding: `0px ${theme.spacing(1.5)}px`,
  borderRadius: '4px',
  letterSpacing: 0.02,
  justifyContent: 'space-between',
  '&:hover': {
    color: theme.palette.hpAccentBlack,
    backgroundColor: theme.palette.hpWhite,
    borderColor: theme.palette.grey[200]
  },
  '&:active': {
    backgroundColor: theme.palette.hpBaseLighter
  },
  '&:focus': {
    borderColor: theme.palette.hpAccentBlack
  },
  backgroundColor: theme.palette.hpWhite
}))

export const StyledGridItem = styled(Grid)(({theme}) => ({
  '&.MuiGrid-item': {
    paddingTop: theme.spacing(1)
  }
}))

export const StyledSelectAllFormControl = styled(FormControl)(({theme}) => ({
  paddingLeft: theme.spacing(1)
}))

export const StyledUseCaseFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'isAdminPanel'
})(({isAdminPanel}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'relative',
  ...(isAdminPanel
    ? {
        width: '100%',
        minWidth: 0,
        '& .MuiFormControlLabel-root': {
          flex: 1,
          minWidth: 0,
          marginRight: 0
        },
        '& .MuiFormControlLabel-label': {
          display: 'block',
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'normal',
          wordBreak: 'break-word'
        }
      }
    : {}),
  '&:hover .info-icon': {
    display: 'inline-block'
  }
}))

export const StyledUseCaseFormControlLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'isAdminPanel'
})(({theme, disabled, isAdminPanel}) => ({
  color: disabled ? theme.palette.hpBase : theme.palette.hpBlack,
  ...(isAdminPanel
    ? {
        overflow: 'visible',
        textOverflow: 'clip',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        minWidth: 0
      }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        whiteSpace: 'nowrap'
      })
}))
export const StyledTopic = styled(Typography)(({theme}) => ({
  padding: theme.spacing(1),
  fontWeight: theme.typography.fontWeightSemibold
}))

export const StyledSubTopic = styled(Typography)(({theme}) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(0)} ${theme.spacing(1)} ${theme.spacing(2)}`
}))

export const StyledTypography = styled(Typography, {shouldForwardProp: (prop) => prop !== 'bold'})(({theme, bold}) => ({
  color: theme.palette.hpBlack,
  fontWeight: bold ? theme.typography.fontWeightSemibold : theme.typography.fontWeightRegular
}))

export const InputTitleItalic = styled('div')(({theme}) => ({
  fontStyle: 'italic',
  color: theme.palette.hpBlack,
  paddingBottom: theme.spacing(0.75)
}))

export const StyledFlexBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
}))

export const StyledSnackbar = styled(Snackbar)(({theme}) => ({
  [`& .MuiSnackbarContent-root`]: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.hpAccentBlack,
    fontSize: theme.typography.subtitle1.fontSize
  }
}))

export const StyledArrowForwardIosSharpIcon = styled(ArrowForwardIosSharp)(({theme}) => ({
  fontSize: theme.typography.subtitleSmall.fontSize
}))

export const StyledNotificationInfoOutlinedIcon = styled(InfoOutlined)(({theme}) => ({
  color: theme.palette.hpAccentPersianGreen,
  marginRight: '10px'
}))

export const StyledNotificationTextBox = styled(Box)(() => ({
  width: '500px'
}))

export const StyledInfoOutlinedIcon = styled(InfoOutlined)(({theme}) => ({
  display: 'none',
  marginLeft: 'auto',
  flexShrink: 0,
  color: theme.palette.hpBaseDark
}))

export const StyledChipBox = styled(Box)(() => ({
  marginLeft: '-28px'
}))

export const StyledChip = styled(Chip)(({theme}) => ({
  marginLeft: theme.spacing(1),
  '& .MuiChip-label': {
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: theme.typography.h6.fontWeight
  }
}))

export const StyledClose = styled(Close)(({theme}) => ({
  color: theme.palette.hpBaseDarker
}))

export const StyledToolTip = styled(({className, ...props}) => <Tooltip {...props} classes={{popper: className}} />)(
  ({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.hpBaseDarker,
      fontSize: theme.typography.listItemMedium.fontSize
    }
  })
)

export const StyledConfirmationWarningIcon = styled(Report)(({theme}) => ({
  color: theme.palette.hpAccentOrange,
  marginRight: '10px',
  marginTop: '2px',
  display: 'flex',
  alignSelf: 'flex-start'
}))

export const StyledDialogTitle = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  fontWeight: theme.typography.fontWeightBold
}))

export const StyledConfirmationDialogButton = styled(Button, {shouldForwardProp: (prop) => prop !== 'buttonType'})(
  ({theme, buttonType}) => ({
    [`&.${buttonClasses.root}`]: {
      marginRight: buttonType === 'cancel' ? '10px' : '15px',
      border: buttonType === 'cancel' ? `1px solid ${theme.palette.hpPrimaryDark}` : 'none',
      color: buttonType === 'cancel' ? theme.palette.hpPrimaryDark : theme.palette.hpWhite,
      backgroundColor: buttonType === 'cancel' ? theme.palette.hpWhite : theme.palette.hpPrimary,
      fontSize: theme.typography.subtitle1.fontSize,
      textTransform: 'none',
      padding: `${theme.spacing(0.5)} ${theme.spacing(5)}`,
      letterSpacing: '0.3px',
      '&:hover': {
        backgroundColor: buttonType === 'cancel' ? theme.palette.hpWhite : theme.palette.hpPrimary,
        color: buttonType === 'cancel' ? theme.palette.hpPrimaryDark : theme.palette.hpWhite
      }
    }
  })
)

export const StyledAccordion = styled((props) => <Accordion disableGutters elevation={0} square {...props} />)(() => ({
  border: 'none',
  '&:before': {
    opacity: 0
  }
}))

export const StyledAccordionSummary = styled((props) => (
  <AccordionSummary expandIcon={<StyledArrowForwardIosSharpIcon />} {...props} />
))(({theme}) => ({
  border: 'none',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    transform: 'rotate(90deg)'
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(270deg)'
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1)
  }
}))

export const StyledAccordionDetails = styled(AccordionDetails)(({theme}) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  border: 'none'
}))

export const DomainHeading = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.hpAccentBlack,
  marginBottom: theme.spacing(5)
}))

export const UseCaseSelectedItemsContainer = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(2.5),
  borderRadius: '4px',
  minHeight: '60px'
}))

export const UseCaseChipsWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  '& .MuiChip-root': {
    backgroundColor: theme.palette.hpWhite,
    color: theme.palette.hpAccentBlack,
    margin: 0
  }
}))

export const UseCaseDomainWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFirst'
})(({theme, isFirst}) => ({
  marginTop: theme.spacing(isFirst ? 3 : 4),
  marginBottom: theme.spacing(2)
}))

export const StyledDeleteIconButton = styled(IconButton)(() => ({
  width: '2px',
  height: '2px'
}))

export const StyledDropdownContainer = styled(Box)(() => ({
  position: 'relative'
}))

export const StyledKeyboardArrowDownIcon = styled(KeyboardArrowDown)(() => ({
  cursor: 'pointer'
}))

export const SelectedLabel = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.subtitle2.fontSize,
  fontStyle: 'italic',
  color: theme.palette.hpBase,
  margin: 0
}))

export const SelectedHeaderRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5)
}))

export const StyledUseCaseCustomLabels = styled(Button)(() => ({
  marginTop: '-8px',
  padding: '8px',
  cursor: 'pointer',
  fontStyle: 'italic',
  background: 'none',
  letterSpacing: '0.25px',
  textDecoration: 'underline',
  '&:hover': {
    background: 'none'
  }
}))
